import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SUGGESTIONS = [
  'Quelle formation pour débuter ?',
  'Formation RCR adulte',
  'Prix et horaires disponibles',
  'Formation pour mon équipe',
];

const STORAGE_KEY_SESSION = 'vs_chat_session';
const STORAGE_KEY_MESSAGES = 'vs_chat_messages';
const MAX_STORED_MESSAGES = 40; // limite pour ne pas saturer localStorage

function getOrCreateSessionId() {
  let id = localStorage.getItem(STORAGE_KEY_SESSION);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(STORAGE_KEY_SESSION, id);
  }
  return id;
}

function loadStoredMessages() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MESSAGES);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveMessages(msgs) {
  try {
    const toStore = msgs.slice(-MAX_STORED_MESSAGES);
    localStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify(toStore));
  } catch { /* localStorage plein, on ignore */ }
}

const STORAGE_KEY_RATE = 'vs_chat_rate';
const RATE_LIMIT = 15;
const RATE_WINDOW = 3_600_000; // 1h

function loadRateState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_RATE);
    if (!raw) return { count: 0, resetAt: Date.now() + RATE_WINDOW };
    const state = JSON.parse(raw);
    if (Date.now() > state.resetAt) return { count: 0, resetAt: Date.now() + RATE_WINDOW };
    return state;
  } catch { return { count: 0, resetAt: Date.now() + RATE_WINDOW }; }
}

function saveRateState(state) {
  try { localStorage.setItem(STORAGE_KEY_RATE, JSON.stringify(state)); } catch {}
}

// Le bandeau cookies est visible tant que l'utilisateur n'a pas cliqué (aucun
// cookie de consentement posé) → on remonte la bulle pour ne pas le recouvrir.
function isCookieBannerVisible() {
  const c = typeof document !== 'undefined' ? document.cookie : '';
  return !/cookie_consent_displayed=/.test(c) && !/cookie_consent=/.test(c);
}


const TypingDots = () => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '10px 14px' }}>
    {[0, 1, 2].map(i => (
      <span key={i} style={{
        width: 8, height: 8, borderRadius: '50%', backgroundColor: '#aaa',
        animation: 'vsDotPulse 1.2s ease-in-out infinite',
        animationDelay: `${i * 0.2}s`,
      }} />
    ))}
  </div>
);

const WELCOME_MSG = {
  role: 'assistant',
  content: 'Bonjour ! 👋 Je suis l\'assistant de l\'Académie Vita Secours.\n\nPour mieux vous orienter, dites-moi : vous cherchez une formation **pour vous-même**, **pour votre enfant**, ou **pour votre entreprise / groupe** ?',
};

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const hasMic = !!SpeechRecognition;

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';

export default function ChatWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const stored = loadStoredMessages();
    return stored.length > 0 ? stored : [WELCOME_MSG];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(() => {
    const rate = loadRateState();
    return Math.max(0, RATE_LIMIT - rate.count);
  });
  const [sessionId] = useState(getOrCreateSessionId);
  const [listening, setListening] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [aiFeaturesEnabled, setAiFeaturesEnabled] = useState(true);
  const [cookieOpen, setCookieOpen] = useState(isCookieBannerVisible);
  const [showHint, setShowHint] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // Vérifier si les fonctionnalités IA sont activées
  useEffect(() => {
    const fetchAiStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/maintenance`);
        if (response.ok) {
          const data = await response.json();
          setAiFeaturesEnabled(data.aiFeaturesEnabled !== false);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut IA:', error);
      }
    };

    fetchAiStatus();
  }, []);

  // Sauvegarde automatique à chaque changement de messages
  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Suivre l'affichage du bandeau cookies (pour ne pas le recouvrir)
  useEffect(() => {
    const update = () => setCookieOpen(isCookieBannerVisible());
    window.addEventListener('vs-cookie-consent', update);
    const iv = setInterval(update, 1500);
    return () => { window.removeEventListener('vs-cookie-consent', update); clearInterval(iv); };
  }, []);

  // Petite bulle d'invitation quelques secondes après l'arrivée (une seule fois)
  useEffect(() => {
    if (open || localStorage.getItem('vs_chat_hint_dismissed') === '1') return;
    const t = setTimeout(() => setShowHint(true), 2500);
    return () => clearTimeout(t);
  }, [open]);

  const dismissHint = () => {
    setShowHint(false);
    try { localStorage.setItem('vs_chat_hint_dismissed', '1'); } catch {}
  };

  const clearHistory = () => {
    window.speechSynthesis.cancel();
    const fresh = [WELCOME_MSG];
    setMessages(fresh);
    saveMessages(fresh);
    const newRate = { count: 0, resetAt: Date.now() + RATE_WINDOW };
    saveRateState(newRate);
    setRemaining(RATE_LIMIT);
  };

  // ── TTS ──────────────────────────────────────────────────────────────────
  const speak = (text, index) => {
    if (!window.speechSynthesis) return;
    // Si ce message est déjà en lecture → stop
    if (speakingIndex === index) {
      window.speechSynthesis.cancel();
      setSpeakingIndex(null);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'fr-FR';
    utterance.rate = 1.05;
    utterance.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const frVoice = voices.find(v => v.lang.startsWith('fr'));
    if (frVoice) utterance.voice = frVoice;
    utterance.onstart = () => setSpeakingIndex(index);
    utterance.onend = () => setSpeakingIndex(null);
    utterance.onerror = () => setSpeakingIndex(null);
    window.speechSynthesis.speak(utterance);
  };

  // Arrêter la synthèse vocale quand on ferme
  const handleClose = () => {
    window.speechSynthesis.cancel();
    setSpeakingIndex(null);
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
    setOpen(false);
  };

  // ── STT ──────────────────────────────────────────────────────────────────
  const toggleListening = () => {
    if (!hasMic) return;
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const history = newMessages.slice(1, -1).map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API_URL}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, history, sessionId }),
      });
      const data = await res.json();

      if (res.status === 429) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "L'assistant est actuellement sollicité par plusieurs personnes en même temps. Veuillez patienter quelques instants et réessayer, ou consultez notre page FAQ pour trouver rapidement une réponse à votre question.",
        }]);
        setRemaining(0);
      } else if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
        if (data.remaining !== undefined) {
          setRemaining(data.remaining);
          const rate = loadRateState();
          saveRateState({ count: RATE_LIMIT - data.remaining, resetAt: rate.resetAt });
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Désolé, une erreur est survenue. Veuillez réessayer.',
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Le service IA est momentanément surchargé. Veuillez réessayer dans quelques secondes.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!aiFeaturesEnabled) return null;

  // Remonte la bulle (et le panneau) au-dessus du bandeau cookies quand il est affiché
  const bubbleBottom = 24 + (cookieOpen ? 72 : 0);

  return (
    <>
      <style>{`
        @keyframes vsDotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes vsSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes vsAttention {
          0%   { box-shadow: 0 4px 20px rgba(198,40,40,0.5), 0 0 0 0 rgba(198,40,40,0.55); }
          70%  { box-shadow: 0 4px 20px rgba(198,40,40,0.5), 0 0 0 18px rgba(198,40,40,0); }
          100% { box-shadow: 0 4px 20px rgba(198,40,40,0.5), 0 0 0 0 rgba(198,40,40,0); }
        }
        @keyframes vsHintIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .vs-chat-bubble.pulse { animation: vsAttention 2s ease-out infinite; }
        .vs-chat-bubble:hover { transform: scale(1.08); }
        .vs-chat-send:hover { background: #a81f1f !important; }
        .vs-suggestion:hover { background: rgba(198,40,40,0.1) !important; color: #c62828 !important; }
      `}</style>

      {/* Bulle d'invitation (attire l'attention) */}
      {showHint && !open && (
        <div
          className="vs-chat-hint"
          onClick={() => { setShowHint(false); setOpen(true); }}
          style={{
            position: 'fixed', right: 24, bottom: bubbleBottom + 74, zIndex: 9998,
            background: '#fff', color: '#333', padding: '10px 30px 10px 14px',
            borderRadius: 14, boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            fontSize: 13.5, fontWeight: 600, cursor: 'pointer', maxWidth: 230,
            border: '1px solid #f0d0d0', animation: 'vsHintIn 0.3s ease-out',
          }}
          title="Ouvrir le chat"
        >
          💬 Une question ? Je peux vous aider !
          <span
            onClick={(e) => { e.stopPropagation(); dismissHint(); }}
            style={{ position: 'absolute', top: 3, right: 8, color: '#bbb', fontSize: 15, cursor: 'pointer' }}
            title="Masquer"
          >×</span>
        </div>
      )}

      {/* Bulle flottante */}
      <button
        className={`vs-chat-bubble${open ? '' : ' pulse'}`}
        onClick={() => open ? handleClose() : setOpen(true)}
        style={{
          position: 'fixed', bottom: bubbleBottom, right: 24, zIndex: 9998,
          width: 60, height: 60, borderRadius: '50%',
          backgroundColor: '#c62828', border: 'none',
          boxShadow: '0 4px 20px rgba(198,40,40,0.5)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.2s, bottom 0.25s',
          fontSize: 26,
        }}
        title="Parler à un conseiller"
      >
        {open ? '✕' : '💬'}
      </button>

      {/* Panneau de chat */}
      {open && (
        <div style={{
          position: 'fixed', bottom: bubbleBottom + 66, right: 24, zIndex: 9997,
          width: 370, height: 520, borderRadius: 16,
          backgroundColor: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'vsSlideUp 0.25s ease-out',
        }}>

          {/* Header */}
          <div style={{
            backgroundColor: '#c62828', padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
            }}>🩺</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Assistant Vitasecours</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                Assistant virtuel
              </div>
            </div>
            <button
              onClick={clearHistory}
              title="Effacer la conversation"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer', padding: '2px 4px' }}
            >
              🗑
            </button>
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50' }} />
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '82%',
                  padding: '9px 13px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  backgroundColor: msg.role === 'user' ? '#c62828' : '#f5f5f5',
                  color: msg.role === 'user' ? '#fff' : '#333',
                  fontSize: 13.5,
                  lineHeight: 1.6,
                }}>
                  {msg.role === 'user' ? (
                    msg.content
                  ) : (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p style={{ margin: '0 0 6px' }}>{children}</p>,
                        strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
                        ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
                        ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
                        li: ({ children }) => <li style={{ marginBottom: 3 }}>{children}</li>,
                        h1: ({ children }) => <strong style={{ fontSize: 14 }}>{children}</strong>,
                        h2: ({ children }) => <strong style={{ fontSize: 14 }}>{children}</strong>,
                        h3: ({ children }) => <strong>{children}</strong>,
                        a: ({ href, children }) => {
                          const isInternal = href && (href.startsWith('/') || href.startsWith(FRONTEND_URL));
                          const path = href?.replace(FRONTEND_URL, '') || href;
                          return isInternal ? (
                            <button
                              onClick={() => { navigate(path); setOpen(false); }}
                              style={{ background: 'none', border: 'none', padding: 0, color: '#c62828', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                            >
                              {children}
                            </button>
                          ) : (
                            <a href={href} target="_blank" rel="noreferrer" style={{ color: '#1565c0', textDecoration: 'underline' }}>{children}</a>
                          );
                        },
                        code: ({ children }) => <code style={{ background: '#e0e0e0', padding: '1px 4px', borderRadius: 3, fontSize: 12 }}>{children}</code>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  )}
                </div>
                {msg.role === 'assistant' && (
                  <button
                    onClick={() => speak(msg.content, i)}
                    title={speakingIndex === i ? 'Arrêter la lecture' : 'Écouter ce message'}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 13, padding: '2px 4px', marginTop: 2, borderRadius: 4,
                      color: speakingIndex === i ? '#c62828' : '#bbb',
                      transition: 'color 0.15s',
                    }}
                    onMouseOver={e => e.currentTarget.style.color = '#c62828'}
                    onMouseOut={e => e.currentTarget.style.color = speakingIndex === i ? '#c62828' : '#bbb'}
                  >
                    {speakingIndex === i ? '⏹' : '🔊'}
                  </button>
                )}
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ backgroundColor: '#f5f5f5', borderRadius: '16px 16px 16px 4px' }}>
                  <TypingDots />
                </div>
              </div>
            )}

            {/* Suggestions (seulement au début) */}
            {messages.length === 1 && !loading && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {SUGGESTIONS.map(s => (
                  <button
                    key={s}
                    className="vs-suggestion"
                    onClick={() => sendMessage(s)}
                    style={{
                      padding: '6px 12px', borderRadius: 20,
                      border: '1px solid rgba(198,40,40,0.3)',
                      backgroundColor: '#fff', color: '#555',
                      fontSize: 12, cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '10px 12px',
            borderTop: '1px solid #eee',
            display: 'flex', gap: 6, alignItems: 'flex-end',
          }}>
            {hasMic && (
              <button
                onClick={toggleListening}
                title={listening ? 'Arrêter l\'écoute' : 'Parler'}
                style={{
                  width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                  border: 'none', cursor: 'pointer', fontSize: 17,
                  backgroundColor: listening ? '#ffebee' : '#f5f5f5',
                  color: listening ? '#c62828' : '#666',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  animation: listening ? 'vsDotPulse 1s ease-in-out infinite' : 'none',
                }}
              >
                {listening ? '⏹' : '🎤'}
              </button>
            )}
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
              }}
              placeholder={remaining > 0 ? 'Posez votre question…' : 'Limite atteinte pour cette heure'}
              disabled={loading || remaining === 0}
              rows={1}
              style={{
                flex: 1, resize: 'none', border: '1px solid #ddd',
                borderRadius: 10, padding: '9px 12px', fontSize: 13.5,
                outline: 'none', fontFamily: 'inherit',
                maxHeight: 100, overflowY: 'auto',
                backgroundColor: remaining === 0 ? '#f9f9f9' : '#fff',
              }}
            />
            <button
              className="vs-chat-send"
              onClick={() => sendMessage()}
              disabled={loading || !input.trim() || remaining === 0}
              style={{
                width: 38, height: 38, borderRadius: '50%',
                backgroundColor: '#c62828', border: 'none',
                color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, transition: 'background 0.2s',
                opacity: (!input.trim() || loading || remaining === 0) ? 0.5 : 1,
                flexShrink: 0,
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
